import { lazy } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  Outlet,
} from "react-router";
import { FundamentalsPageByLocation } from "@features/fundamentals/FundamentalsPageByLocation.tsx";
import { DepotTransferPage } from "@features/depots/depotTransfer/DepotTransferPage.tsx";
import {
  SynchronizationLogListWithRouting,
  AppLayout,
  FeedbackButton,
} from "@components";
import { MandatorConditional } from "@helpers";
import { KpmgPrivacyConsentModal } from "@mandatorFeatures/kpmg/dashboard/KpmgPrivacyConsentModal";
import { AccountReviewModal } from "@features/accountReview/AccountReviewModal";
import { ReferenceAccountEditPage } from "@features/depots/referenceAccount/ReferenceAccountEditPage.tsx";
import { FileDropHandlerWithRoutingPage } from "@features/fileDrop/FileDropHandlerWithRoutingPage.tsx";
import { ImportSuccessWithRedirectPage } from "@features/fileDrop/ImportSuccessWithRedirectPage.tsx";
import { Impersonate } from "@features/login/impersonate/Impersonate.tsx";
import { NewTransactionWizardWithRouting } from "@features/transactionWizard/NewTransactionWizardWithRouting.tsx";
import { EditTransactionWizardWithRouting } from "@features/transactionWizard/EditTransactionWizardWithRouting.tsx";
import { AddTransactionWizardWithRouting } from "@features/transactionWizard/AddTransactionWizardWithRouting.tsx";
import { CreateDividendWizardWithRouting } from "@features/dividendWizard/CreateDividendWizardWithRouting.tsx";
import { CreateBatchDividendWizardWithRouting } from "@features/dividendWizard/CreateBatchDividendWizardWithRouting.tsx";
import { ManualTransactionWizardWithRouting } from "@features/transactionWizard/ManualTransactionWizardWithRouting.tsx";
import { InvestmentsPage } from "@features/investments/InvestmentsPage.tsx";
import { DepotPage } from "@features/depots/depot/DepotPage.tsx";
import { EditDividendWizardWithRouting } from "@features/dividendWizard/EditDividendWizardWithRouting.tsx";
import { InvestmentEditByParamPage } from "@features/investments/investment/edit/InvestmentEditByParamPage.tsx";
import { SingleAccountByParamPage } from "@features/accounts/account/SingleAccountByParamPage.tsx";
import { AddManuallyPage } from "@features/accounts/transactions/AddManuallyPage.tsx";
import { AccountEditPage } from "@features/accounts/edit/AccountEditPage.tsx";
import { DepotEditPage } from "@features/depots/edit/DepotEditPage.tsx";
import { SingleSecurityPage } from "@features/securities/security/SingleSecurityPage.tsx";
import { AppLayoutProvider } from "@providers";
import { CurrencyDisplayProvider } from "@providers/CurrencyDisplayProvider";
import { DepotsAccountsPage } from "@features/depots/DepotsAccountsPage.tsx";
import { ScrollToTop } from "@rentablo/components";
import { SearchSecurityPage } from "@features/securities/SearchSecurityPage.tsx";
import { CsvUploadPage } from "@features/csvDrop/CsvUploadPage.tsx";
import { DashboardSelectedGroupsProvider } from "@features/dashboard/DashboardSelectedGroupProvider.tsx";
import { InvestmentByParamPage } from "@features/investments/investment/components";
import {
  nonTppWebFormRoutes,
  tppWebFormRoutes,
  mockRoutes,
} from "@rentablo/bank-import";
import { RecommenderPage } from "@features/recommender/RecommenderPage/RecommenderPage.tsx";
import { BankSelectWithRedirectPage } from "@features/bankSelect/BankSelectWithRedirectPage";
import { WebFormEntryGate } from "@features/bankImport/WebFormEntryGate";
import { BankImportStatusProvider } from "@providers/BankImportStatusProvider/BankImportStatusProvider.tsx";
import { ErrorBoundaryRouter } from "@components/common/errorBoundaries/ErrorBoundaryRouter.tsx";
import { dashboardComponents } from "./mandatorComponents.tsx";
import {
  OnlyAuthenticatedRoute,
  WithComponentByMandator,
  withSuspense,
  WithFeatureFlagRoute,
} from "./routes.tsx";
import {
  adminRoutes,
  cashbackRoutes,
  authenticationRoutes,
  legalRoutes,
  settingsRoutes,
  watchlistRoutes,
  goalsRoutes,
  analyzerRoutes,
} from "./groups";

const ClassificationsPage = lazy(() =>
  import("@features/classifications/ClassificationsPage.tsx").then(
    (module) => ({
      default: module.ClassificationsPage,
    })
  )
);

const DividendPlannerPage = lazy(() =>
  import("@features/dividendPlanner/DividendPlannerPage.tsx").then(
    (module) => ({
      default: module.DividendPlannerPage,
    })
  )
);

const MainApplicationRedirect = () => {
  window.location.href = `${import.meta.env.VITE_APP_ROOT_URL}/app`;
  return null;
};

const RootLayout = () => {
  return (
    <BankImportStatusProvider>
      <Outlet />
      <FeedbackButton />
      <ScrollToTop />
      <MandatorConditional showFor={["kpmg-germany"]}>
        <KpmgPrivacyConsentModal />
        <AccountReviewModal />
      </MandatorConditional>
    </BankImportStatusProvider>
  );
};

const routes = [
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorBoundaryRouter />,
    children: [
      {
        path: "fundamentals",
        element: <FundamentalsPageByLocation />,
      },
      {
        path: "mainApplication",
        element: <MainApplicationRedirect />,
      },
      {
        path: "import-bank-connection/select-bank",
        element: <BankSelectWithRedirectPage />,
      },
      {
        path: "login/impersonate/:userId",
        element: (
          <OnlyAuthenticatedRoute>
            <Impersonate />
          </OnlyAuthenticatedRoute>
        ),
      },
      {
        path: "synchronization-logs/:accountId",
        element: (
          <OnlyAuthenticatedRoute>
            <SynchronizationLogListWithRouting />
          </OnlyAuthenticatedRoute>
        ),
      },
      {
        path: "search/securities",
        element: <SearchSecurityPage />,
      },
      {
        path: "depots/:fromAccountId/transfer",
        element: (
          <OnlyAuthenticatedRoute>
            <DepotTransferPage />
          </OnlyAuthenticatedRoute>
        ),
      },
      {
        path: "recommender/:isin",
        element: <RecommenderPage />,
      },
      {
        path: "",
        element: (
          <OnlyAuthenticatedRoute>
            <AppLayoutProvider>
              <CurrencyDisplayProvider>
                <AppLayout />
              </CurrencyDisplayProvider>
            </AppLayoutProvider>
          </OnlyAuthenticatedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <DashboardSelectedGroupsProvider>
                <WithComponentByMandator {...dashboardComponents} />
              </DashboardSelectedGroupsProvider>
            ),
          },

          {
            path: "investments",
            children: [
              {
                index: true,
                element: <InvestmentsPage />,
              },
              {
                path: ":investmentId/edit",
                element: <InvestmentEditByParamPage />,
              },
              {
                path: ":investmentId",
                element: <InvestmentByParamPage />,
              },
              {
                path: "file-drop",
                element: (
                  <WithFeatureFlagRoute featureFlag="PDF_IMPORT">
                    <FileDropHandlerWithRoutingPage />
                  </WithFeatureFlagRoute>
                ),
              },
              {
                path: "csv-drop",
                element: <CsvUploadPage />,
              },
              {
                path: "file-drop-success/:depotSynchronizationLogId",
                element: (
                  <WithFeatureFlagRoute featureFlag="PDF_IMPORT">
                    <ImportSuccessWithRedirectPage />
                  </WithFeatureFlagRoute>
                ),
              },
              {
                path: ":investmentId/dividend-wizard/create/batch",
                element: <CreateBatchDividendWizardWithRouting />,
              },
              {
                path: ":investmentId/dividend-wizard/create",
                element: <CreateDividendWizardWithRouting />,
              },
              {
                path: ":investmentId/dividend-wizard/edit/:bookingId",
                element: <EditDividendWizardWithRouting />,
              },
            ],
          },
          {
            path: "depots-accounts",
            element: <DepotsAccountsPage />,
          },
          {
            path: "depots",
            children: [
              {
                index: true,
                element: <Navigate to="/depots-accounts" replace />,
              },
              {
                path: ":depotId/edit",
                element: <DepotEditPage />,
              },
              {
                path: ":depotId/reference-account",
                element: <ReferenceAccountEditPage />,
              },
              {
                path: ":depotId",
                element: <DepotPage />,
              },
              {
                path: ":depotId/investments/:investmentId",
                element: <InvestmentByParamPage />,
              },
              {
                path: ":depotId/csv-drop",
                element: <CsvUploadPage />,
              },
              {
                path: ":accountId/file-drop",
                element: (
                  <WithFeatureFlagRoute featureFlag="PDF_IMPORT">
                    <FileDropHandlerWithRoutingPage />
                  </WithFeatureFlagRoute>
                ),
              },
              {
                path: ":accountId/file-drop-success/:depotSynchronizationLogId",
                element: (
                  <WithFeatureFlagRoute featureFlag="PDF_IMPORT">
                    <ImportSuccessWithRedirectPage />
                  </WithFeatureFlagRoute>
                ),
              },
            ],
          },
          {
            path: "accounts",
            children: [
              {
                index: true,
                element: <Navigate to="/depots-accounts" replace />,
              },
              {
                path: ":accountId/transactions/add-manually",
                element: <AddManuallyPage />,
              },
              {
                path: ":accountId/edit",
                element: <AccountEditPage />,
              },
              {
                path: ":accountId",
                element: <SingleAccountByParamPage />,
              },
            ],
          },
          ...watchlistRoutes,
          ...analyzerRoutes,
          ...goalsRoutes,
          {
            path: "classifications",
            element: (
              <WithFeatureFlagRoute featureFlag="CLASSIFICATIONS">
                {withSuspense(ClassificationsPage)}
              </WithFeatureFlagRoute>
            ),
          },
          {
            path: "dividend-planner",
            element: (
              <WithFeatureFlagRoute featureFlag="DIVIDEND_PLANNER">
                {withSuspense(DividendPlannerPage)}
              </WithFeatureFlagRoute>
            ),
          },
          {
            path: "securities",
            element: <SingleSecurityPage />,
          },
          {
            path: "new-transaction-wizard",
            element: <NewTransactionWizardWithRouting />,
          },
          {
            path: "manual-transaction-wizard",
            element: <ManualTransactionWizardWithRouting />,
          },
          {
            path: "edit-transaction-wizard/:id",
            element: <EditTransactionWizardWithRouting />,
          },
          {
            path: "add-transaction-wizard/:id",
            element: <AddTransactionWizardWithRouting />,
          },
          ...adminRoutes,
        ],
      },
      ...authenticationRoutes,
      ...legalRoutes,
      ...settingsRoutes,
      ...cashbackRoutes,
      ...mockRoutes,
      {
        path: "",
        element: (
          <OnlyAuthenticatedRoute>
            <WebFormEntryGate />
          </OnlyAuthenticatedRoute>
        ),
        children: nonTppWebFormRoutes,
      },
      ...tppWebFormRoutes,
    ],
  },
];

export const router = createBrowserRouter(routes, {
  basename: import.meta.env.VITE_PUBLIC_URL,
});

export const AppRouter = () => {
  return <RouterProvider router={router} />;
};
